/**
 * MFA challenge screen (Req 22.8, 22.9, 22.10).
 *
 * Presents the second-factor step: a 6-digit code field whose value submits to
 * the Identity_Service, and accessible {@link UserMessage}s for an invalid code
 * (the field stays editable so the user can correct it) and for the
 * too-many-attempts / rate-limit condition (the retry guidance never surfaces a
 * raw code alone — the UserMessage contract enforces what/why/next).
 *
 * Web boundary: the Identity_Service second-factor verification is injected as
 * `onSubmitCode`; the screen never imports backend runtime code. The registry
 * renders it with no props for SSR, so the prop is optional and the form renders
 * standalone.
 *
 * All text resolves through `useT()` (Req 22.11). SSR-friendly: the first render
 * is a pure function of props; interactivity activates on hydration.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/**
 * The distinct outcomes of submitting a second factor (Req 22.9). `ok`
 * establishes the session; `invalid` keeps the field editable to retry;
 * `tooManyAttempts` reports the 5-invalid-code cutoff / rate limit.
 */
export type MfaChallengeResult = "ok" | "invalid" | "tooManyAttempts";

/** Props for {@link MfaChallengeScreen}. All optional so it renders standalone. */
export interface MfaChallengeScreenProps {
  /**
   * Injected Identity_Service second-factor verification. Receives the entered
   * code and resolves to a {@link MfaChallengeResult}. Omitted in SSR / the
   * registry, where the form renders but does not submit.
   */
  readonly onSubmitCode?: (code: string) => Promise<MfaChallengeResult>;
  /** An initial result to render on first paint (e.g. after a redirect). */
  readonly initialResult?: MfaChallengeResult;
}

/** The non-`ok` results that carry a User_Message, and their catalog id prefix. */
const RESULT_MESSAGE_KEYS = {
  invalid: "auth.mfa.invalid",
  tooManyAttempts: "auth.mfa.tooManyAttempts",
} as const;

export default function MfaChallengeScreen({
  onSubmitCode,
  initialResult,
}: MfaChallengeScreenProps = {}): ReactElement {
  const t = useT();
  const [code, setCode] = useState("");
  const [result, setResult] = useState<MfaChallengeResult | undefined>(
    initialResult,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (onSubmitCode === undefined) {
      return;
    }
    const outcome = await onSubmitCode(code);
    setResult(outcome);
  }

  const message = toMfaChallengeMessage(result, t);

  return (
    <section aria-labelledby="screen-heading" data-screen="mfaChallenge">
      <h1 id="screen-heading">{t("auth.mfa.title")}</h1>
      <p>{t("auth.mfa.prompt")}</p>

      {message !== undefined ? (
        <UserMessage message={message} severity="error" />
      ) : null}

      <form onSubmit={handleSubmit} noValidate data-testid="mfa-challenge-form">
        <div className="field">
          <label htmlFor="mfa-code">{t("auth.mfa.codeLabel")}</label>
          <input
            id="mfa-code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e): void => setCode(e.target.value)}
          />
        </div>

        <button type="submit" data-testid="mfa-challenge-submit">
          {t("auth.mfa.submit")}
        </button>
      </form>
    </section>
  );
}

/**
 * Build the {@link UserMessageModel} for a non-`ok` MFA challenge result by
 * resolving the matching catalog ids (what/why/next). Returns `undefined` for no
 * result or `"ok"`. Pure and SSR-safe.
 */
export function toMfaChallengeMessage(
  result: MfaChallengeResult | undefined,
  t: (id: string) => string,
): UserMessageModel | undefined {
  if (result === undefined || result === "ok") {
    return undefined;
  }
  const prefix = RESULT_MESSAGE_KEYS[result];
  return {
    what: t(`${prefix}.what`),
    why: t(`${prefix}.why`),
    next: t(`${prefix}.next`),
  };
}
